import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import requiredIf from 'react-required-if';
import {IconProps} from '@nib/icons';
import {linkLoud, linkDark, linkLight, linkDestructive, focusOutlineColor, focusOutlineStyle, focusOutlineWidth, focusOutlineOffset} from '@nib-components/theme';

export const validColorValues = ['loud', 'dark', 'light', 'destructive'] as const;
type colorValues = (typeof validColorValues)[number];

export const validIconPlacementValues = ['left', 'right'] as const;
type iconPlacementValues = (typeof validIconPlacementValues)[number];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const IconAlignment = styled(({iconPlacement, ...props}) => <span {...props} />)`
  vertical-align: sub;

  ${({iconPlacement}) =>
    iconPlacement === 'left'
      ? css`
          margin-right: 0.5rem;
        `
      : css`
          margin-left: 0.5rem;
        `};
`;
export interface LinkProps {
  component?: any;
  href?: string;
  color?: colorValues;
  underline?: boolean;
  icon?: React.FC<IconProps>;
  iconPlacement?: iconPlacementValues;
  [key: string]: unknown; // other props
}

export const BaseLink: React.FC<LinkProps> = React.forwardRef((props, ref) => {
  const {
    component: Component,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    color = 'loud',
    icon: Icon,
    iconPlacement,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    underline,
    children,
    ...otherProps
  } = props;

  return Icon ? (
    <Component ref={ref} {...otherProps}>
      {iconPlacement === 'left' && (
        <IconAlignment iconPlacement={iconPlacement}>
          <Icon size="xs" fill="currentColor" />
        </IconAlignment>
      )}
      <span>{children}</span>
      {iconPlacement === 'right' && (
        <IconAlignment iconPlacement={iconPlacement}>
          <Icon size="xs" fill="currentColor" />
        </IconAlignment>
      )}
    </Component>
  ) : (
    <Component ref={ref} {...otherProps}>
      {children}
    </Component>
  );
});

BaseLink.propTypes = {
  // 'any' is required due to our use of GatsbyJS's Link component which doesn't fall under any defined PropType.
  component: PropTypes.any,
  href: requiredIf(PropTypes.string, (props: {component: any}) => props.component === 'a'),
  color: PropTypes.oneOf(validColorValues),
  icon: PropTypes.func,
  iconPlacement: PropTypes.oneOf(validIconPlacementValues),
  underline: PropTypes.bool
};

BaseLink.defaultProps = {
  component: 'a'
};

const loudLink = css`
  color: ${linkLoud};
`;
const darkLink = css`
  color: ${linkDark};
`;
const lightLink = css`
  color: ${linkLight};
`;
const destructiveLink = css`
  color: ${linkDestructive};
`;

const Link = styled(BaseLink)<HTMLAnchorElement>`
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 0.125em; /* 2px at 16px */
  text-decoration-thickness: min(0.125em, 4px); /* 2px at 16px */

  /* Handle long links (like urls) */
  /* These are technically the same, but use both */
  overflow-wrap: break-word;
  word-wrap: break-word;

  -ms-word-break: break-all;
  /* This is the dangerous one in WebKit, as it breaks things wherever */
  word-break: break-all;
  /* Instead use this non-standard one: */
  word-break: break-word;

  /* Subtly more weight for links, defaults to 400 font weight if 500 is not available for a brand */
  font-weight: 500;

  /* By default, use the loudLink */
  ${({color}) => !validColorValues.includes(color as colorValues) && loudLink};

  /* Otherwise we set the color to one of these three */
  ${({color}) => color === 'loud' && loudLink};
  ${({color}) => color === 'dark' && darkLink};
  ${({color}) => color === 'light' && lightLink};
  ${({color}) => color === 'destructive' && destructiveLink};

  ${({underline}) => !underline && `text-decoration: none;`}

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineOffset};
  }
`;

Link.propTypes = {
  color: PropTypes.oneOf(validColorValues),
  underline: PropTypes.bool,
  icon: PropTypes.func,
  iconPlacement: PropTypes.oneOf(validIconPlacementValues)
};

Link.defaultProps = {
  color: 'loud',
  iconPlacement: 'right',
  underline: true
};

Link.displayName = 'Link';

export default Link;
